// packages/ui/src/components/InlineEditInput.tsx
import { useEffect, useRef, type ReactElement } from 'react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  ariaLabel: string;
  /** Optional placeholder shown when the value is empty. */
  placeholder?: string;
  /** Visual class. Defaults to the sidebar tree's rename style. */
  className?: string;
  /**
   * `select`: select the entire value on mount.
   * `stem`: select up to the last dot (file-rename default — preserves extension).
   * `none`: leave the caret at the end.
   */
  selectOnMount?: 'select' | 'stem' | 'none';
}

/**
 * Reusable inline-rename text input. Commits on Enter/blur, cancels on Escape,
 * and double-fire-guards the commit so Enter followed by an auto-blur doesn't
 * trigger the parent handler twice. Lives at the components root because both
 * the file tree (sidebar) and the tab strips (open documents, tool panel) all
 * mount it in-place where the label used to be.
 */
export function InlineEditInput({
  value,
  onChange,
  onCommit,
  onCancel,
  ariaLabel,
  placeholder,
  className = 'mp-tree__rename',
  selectOnMount = 'stem',
}: Props): ReactElement {
  const ref = useRef<HTMLInputElement | null>(null);
  const committed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (selectOnMount === 'select') {
      el.select();
      return;
    }
    if (selectOnMount === 'none') return;
    const dot = el.value.lastIndexOf('.');
    if (dot > 0) el.setSelectionRange(0, dot);
    else el.select();
  }, [selectOnMount]);

  return (
    <input
      ref={ref}
      className={className}
      type="text"
      value={value}
      aria-label={ariaLabel}
      placeholder={placeholder}
      spellCheck={false}
      autoCorrect="off"
      autoCapitalize="off"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (committed.current) return;
          committed.current = true;
          onCommit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          if (committed.current) return;
          committed.current = true;
          onCancel();
        } else {
          e.stopPropagation();
        }
      }}
      onBlur={() => {
        if (committed.current) return;
        committed.current = true;
        onCommit();
      }}
    />
  );
}
